import { v4 as uuidv4 } from "uuid";
import { useApiBlocksStore } from "@/stores/apiBlocks";

const API_BLOCK_TAG_NAMES = ["mj-feed-block", "mj-product", "mj-article"];

const IMAGE_META_KEYS = ["image-alt", "image-title"];

type LegacyLayout = "one-column" | "image-text" | "text-image" | string;

function mapLayout(
  legacy: LegacyLayout | undefined
): "column" | "image-text" | "text-image" {
  if (legacy === "image-text") return "image-text";
  if (legacy === "text-image") return "text-image";
  return "column";
}

function isApiBlockTag(tagName: unknown): boolean {
  return typeof tagName === "string" && API_BLOCK_TAG_NAMES.includes(tagName);
}

function needsMigration(block: any): boolean {
  if (!isApiBlockTag(block?.tagName)) return false;
  return (
    !("mode" in block) ||
    !("items" in block) ||
    !("layout" in block) ||
    !Array.isArray(block.children) ||
    block.children.length === 0
  );
}

function buildDefaultColumn() {
  return {
    tagName: "mj-column",
    uid: uuidv4(),
    attributes: {
      "vertical-align": "top",
      padding: "0",
      width: "100%",
    },
    children: [],
  };
}

function copyIfSet(
  target: Record<string, any>,
  source: Record<string, any>,
  key: string
) {
  const val = source[key];
  if (val !== undefined && val !== null && val !== "") {
    target[key] = val;
  }
}

function createItem(
  legacyAttrs: Record<string, any>,
  blockName: string
): Record<string, any> {
  const apiBlocksStore = useApiBlocksStore();
  const blockProperties = apiBlocksStore.blocks.get(blockName);
  const item: Record<string, any> = {};

  const structureKeys = Object.keys(
    blockProperties?.blockStructure ?? {}
  ).filter((k) => k !== "layout");

  for (const key of structureKeys) {
    copyIfSet(item, legacyAttrs, key);
    copyIfSet(item, legacyAttrs, `raw-${key}`);
  }

  for (const metaKey of IMAGE_META_KEYS) {
    copyIfSet(item, legacyAttrs, metaKey);
  }

  if (item.title && !item["image-alt"]) {
    item["image-alt"] = item["raw-title"] || item.title;
  }

  item.id = uuidv4();
  item.name = item["raw-title"] || item.title || blockName;

  const idKey = `${blockName}_id`;
  const feedIdKey = `${blockName}_feed_id`;
  copyIfSet(item, legacyAttrs, idKey);
  copyIfSet(item, legacyAttrs, feedIdKey);

  return item;
}

export function migrateApiBlock(block: any): any {
  if (!needsMigration(block)) return block;

  const attrs = { ...(block.attributes || {}) };
  const legacyLayout = attrs["columns-layout"];
  const layout = mapLayout(legacyLayout);
  const blockName =
    block.blockName ?? (block.tagName === "mj-article" ? "article" : "product");
  delete attrs["columns-layout"];

  const items =
    Array.isArray(block.items) && block.items.length > 0
      ? block.items
      : [createItem(block.attributes || {}, blockName)];

  return {
    ...block,
    tagName: "mj-feed-block",
    blockName: blockName,
    attributes: attrs,
    items,
    mode: block.mode === "dynamic" ? "dynamic" : "static",
    layout,
    maxPerRow: typeof block.maxPerRow === "number" ? block.maxPerRow : 1,
    forMergeTag: block.forMergeTag,
    children:
      Array.isArray(block.children) && block.children.length > 0
        ? block.children
        : [buildDefaultColumn()],
  };
}

export function migrateApiBlocksInTree(mjml: any) {
  const transformedMjml = JSON.parse(JSON.stringify(mjml));
  transformedMjml.children[0].children.forEach((section: any) => {
    section.children.forEach((column: any) => {
      column.children.forEach((block: any, bIndex: number) => {
        column.children[bIndex] = migrateApiBlock(block);
      });
    });
  });
  return transformedMjml;
}
